import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthModal = ({ isOpen, onClose, initialForm = 'login' }) => {
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [welcomePopup, setWelcomePopup] = useState({ show: false, name: '', role: '' });

  const [loginData, setLoginData] = useState({
    contact: "",
    password: "",
  });

  const [signupData, setSignupData] = useState({
    name: "",
    contact: "",
    password: "",
    confirmPassword: "",
    role: "employee"
  });

  const showWelcomePopupMessage = (name, role) => {
    setWelcomePopup({ show: true, name, role });
    setTimeout(() => {
      setWelcomePopup({ show: false, name: '', role: '' });
    }, 3000);
  };

  const getCompleteUserData = (contact, name, role) => {
    const roleData = {
      employee: {
        position: "Software Developer",
        department: "Engineering",
        employeeId: `EMP${Math.floor(Math.random() * 1000)}`,
        joinDate: new Date().toISOString().split('T')[0]
      },
      manager: {
        position: "Department Manager",
        department: "Management",
        employeeId: `MGR${Math.floor(Math.random() * 1000)}`,
        joinDate: new Date().toISOString().split('T')[0]
      },
      hr: {
        position: "HR Manager",
        department: "Human Resources",
        employeeId: `HR${Math.floor(Math.random() * 1000)}`,
        joinDate: new Date().toISOString().split('T')[0]
      },
      admin: {
        position: "System Administrator",
        department: "IT Administration",
        employeeId: `ADM${Math.floor(Math.random() * 1000)}`,
        joinDate: new Date().toISOString().split('T')[0]
      },
      superadmin: {
        position: "Super Administrator",
        department: "Executive Management",
        employeeId: `SA${Math.floor(Math.random() * 1000)}`,
        joinDate: new Date().toISOString().split('T')[0]
      }
    };

    const defaultData = roleData[role] || roleData.employee;

    return {
      id: Math.floor(Math.random() * 1000),
      name: name || contact.split('@')[0] || 'User',
      email: contact.includes('@') ? contact : `${contact}@example.com`,
      contact: contact,
      role: role,
      userRole: role,
      position: defaultData.position,
      department: defaultData.department,
      employeeId: defaultData.employeeId,
      joinDate: defaultData.joinDate,
      phone: '+91 98765 43210',
      dob: '1990-01-01',
      status: 'active'
    };
  };

  const validate = () => {
    const newErrors = {};

    if (activeForm === "login") {
      if (!loginData.contact.trim()) {
        newErrors.contact = "Email or phone is required";
      }
      if (!loginData.password) {
        newErrors.password = "Password is required";
      }
    }

    if (activeForm === "signup") {
      if (!signupData.name.trim()) {
        newErrors.name = "Name is required";
      } else if (signupData.name.trim().length < 2) {
        newErrors.name = "Name must be at least 2 characters";
      }

      if (!signupData.contact.trim()) {
        newErrors.contact = "Contact is required";
      }

      if (!signupData.password) {
        newErrors.password = "Password is required";
      } else if (signupData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }

      if (signupData.password !== signupData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrors({});
    setSuccess('');

    if (!validate()) {
      return;
    }

    setLoading(true);

    // Simulate network delay
    setTimeout(() => {
      if (activeForm === "login") {
        // Direct login - any credentials work
        const userRole = loginData.contact.includes('admin') ? 'admin' :
                        loginData.contact.includes('hr') ? 'hr' :
                        loginData.contact.includes('manager') ? 'manager' : 'employee';
        
        const userName = loginData.contact.split('@')[0].split('+')[0];
        
        const userData = getCompleteUserData(loginData.contact, userName, userRole);
        
        localStorage.setItem('token', 'dummy-token-' + Date.now());
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('userContact', loginData.contact);
        
        setSuccess('Login successful! Redirecting to dashboard...');
        showWelcomePopupMessage(userData.name, userRole);
        
        setLoginData({ contact: "", password: "" });
        
        setTimeout(() => {
          onClose(true);
          setSuccess('');
          setActiveForm('login');
          navigate('/dashboard');
        }, 1500);
      } else {
        // Signup - create new account
        const userRole = signupData.role;
        const userData = getCompleteUserData(signupData.contact, signupData.name, userRole);
        
        // Store in localStorage as "registered" user
        const registeredUsers = JSON.parse(localStorage.getItem('registeredUsers') || '[]');
        registeredUsers.push({
          contact: signupData.contact,
          password: signupData.password,
          name: signupData.name,
          role: userRole
        });
        localStorage.setItem('registeredUsers', JSON.stringify(registeredUsers));
        
        setSuccess('Account created successfully! Please login with your credentials.');
        
        setSignupData({ 
          name: "", 
          contact: "", 
          password: "", 
          confirmPassword: "", 
          role: "employee" 
        });
        
        setTimeout(() => {
          setActiveForm('login');
          setSuccess('');
        }, 2000);
      }
      setLoading(false);
    }, 1000);
  };

  const switchForm = (form) => {
    setActiveForm(form);
    setErrors({});
    setSuccess('');
    if (form === "login") {
      setLoginData({ contact: "", password: "" });
    } else {
      setSignupData({ name: "", contact: "", password: "", confirmPassword: "", role: "employee" });
    }
  };

  const handleClose = () => {
    onClose(false);
    setActiveForm('login');
    setErrors({});
    setSuccess('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-purple-900/70 to-black/80 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Welcome Popup */}
      {welcomePopup.show && (
        <div className="fixed top-5 right-5 z-50 animate-slide-in-right">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-bold">Welcome {welcomePopup.name}!</p>
                <p className="text-sm opacity-90">Role: {welcomePopup.role.toUpperCase()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white p-6 sm:p-8 md:p-10 rounded-2xl 
                    shadow-2xl border border-gray-200 
                    animate-[fadeIn_0.4s_ease-in-out] mx-4">

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 hover:text-black text-lg sm:text-xl"
        >
          ✕
        </button>

        {/* Success message */}
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Error message */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {errors.general}
          </div>
        )}

        {/* Login Form */}
        {activeForm === "login" && (
          <form onSubmit={handleSubmit} className="text-center">
            <h3 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8 text-gray-800">
              Welcome Back
            </h3>

            <div className="flex flex-col items-center space-y-4 sm:space-y-5">
              <div className="w-full">
                <input
                  type="text"
                  name="contact"
                  placeholder="Email or Phone"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.contact ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={loginData.contact}
                  onChange={handleLoginChange}
                  disabled={loading}
                />
                {errors.contact && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.contact}</p>
                )}
              </div>

              <div className="w-full">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.password ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={loginData.password}
                  onChange={handleLoginChange}
                  disabled={loading}
                />
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.password}</p>
                )}
              </div>

              <button 
                type="submit"
                disabled={loading}
                className="w-full py-2 sm:py-3 rounded-lg 
                         bg-black text-white hover:bg-gray-800 
                         transition duration-300 font-medium tracking-wide 
                         text-sm sm:text-base disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Login'}
              </button>

              <button
                type="button"
                className="text-cyan-500 hover:underline text-sm"
                onClick={() => switchForm("signup")}
              >
                Don't have an account? Sign up
              </button>
            </div>
          </form>
        )}

        {/* Signup Form */}
        {activeForm === "signup" && (
          <form onSubmit={handleSubmit} className="text-center">
            <h3 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8 text-gray-800">
              Create Account
            </h3>

            <div className="flex flex-col items-center space-y-4 sm:space-y-5">
              <div className="w-full">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.name ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={signupData.name}
                  onChange={handleSignupChange}
                  disabled={loading}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.name}</p>
                )}
              </div>

              <div className="w-full">
                <input
                  type="text"
                  name="contact"
                  placeholder="Email or Phone"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.contact ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={signupData.contact}
                  onChange={handleSignupChange}
                  disabled={loading}
                />
                {errors.contact && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.contact}</p>
                )}
              </div>

              <div className="w-full">
                <input
                  type="password"
                  name="password"
                  placeholder="Password (min 6 characters)"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.password ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={signupData.password}
                  onChange={handleSignupChange}
                  disabled={loading}
                />
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.password}</p>
                )}
              </div>

              <div className="w-full">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} 
                           text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base`}
                  value={signupData.confirmPassword}
                  onChange={handleSignupChange}
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Role Selection */}
              <div className="w-full">
                <select
                  name="role"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg 
                           border border-gray-300 text-black bg-white
                           focus:outline-none focus:ring-2 focus:ring-black text-sm sm:text-base"
                  value={signupData.role}
                  onChange={handleSignupChange}
                  disabled={loading}
                >
                  <option value="employee">Employee</option>
                  <option value="hr">HR</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
                <p className="text-gray-400 text-xs mt-1 text-left">
                  Select your role
                </p>
              </div>

              <button 
                type="submit"
                disabled={loading}
                className="w-full py-2 sm:py-3 rounded-lg 
                         bg-black text-white hover:bg-gray-800 
                         transition duration-300 font-medium tracking-wide 
                         text-sm sm:text-base disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Sign Up'}
              </button>

              <button
                type="button"
                className="text-cyan-500 hover:underline text-sm"
                onClick={() => switchForm("login")}
              >
                Already have an account? Login
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AuthModal;



// import { useState, useEffect } from 'react';
// import authService from '../services/authService';
// import { useNavigate } from 'react-router-dom';

// // Import images
// import img1 from '../assets/images/a.jpeg';
// import img2 from '../assets/images/ab.jpeg';
// import img3 from '../assets/images/abc.jpeg';
// import img4 from '../assets/images/abcd.jpeg';

// const AuthModal = ({ isOpen, onClose, initialForm = 'login' }) => {
//   const navigate = useNavigate();
//   const [activeForm, setActiveForm] = useState(initialForm);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [success, setSuccess] = useState('');
//   const [welcomePopup, setWelcomePopup] = useState({ show: false, name: '', role: '' });
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   const [isAnimating, setIsAnimating] = useState(false);

//   const [loginData, setLoginData] = useState({
//     contact: "",
//     password: "",
//   });

//   const [signupData, setSignupData] = useState({
//     name: "",
//     contact: "",
//     password: "",
//     confirmPassword: "",
//     role: "employee"
//   });

//   // Slideshow images data
//   const slideshowImages = [
//     { 
//       src: img1, 
//       title: "AI-Powered Security", 
//       description: "Advanced facial recognition and authentication system",
//       tagline: "Secure Your Workspace"
//     },
//     { 
//       src: img2, 
//       title: "Smart Attendance", 
//       description: "Real-time employee tracking with AI verification",
//       tagline: "Track with Intelligence"
//     },
//     { 
//       src: img3, 
//       title: "HR Automation", 
//       description: "Streamline HR operations with smart workflows",
//       tagline: "Automate HR Tasks"
//     },
//     { 
//       src: img4, 
//       title: "Analytics Dashboard", 
//       description: "Comprehensive insights and performance metrics",
//       tagline: "Data-Driven Decisions"
//     }
//   ];

//   // Auto-rotate images every 3 seconds
//   useEffect(() => {
//     if (!isOpen) return;
    
//     const interval = setInterval(() => {
//       setIsAnimating(true);
//       setTimeout(() => {
//         setCurrentImageIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
//         setTimeout(() => setIsAnimating(false), 100);
//       }, 300);
//     }, 3000); // Changed to 3 seconds

//     return () => clearInterval(interval);
//   }, [isOpen]);

//   // Complete user data mapping with all fields
//   const getCompleteUserData = (user) => {
//     const roleData = {
//       employee: {
//         position: "Software Developer",
//         department: "Engineering",
//         employeeId: `EMP${Math.floor(Math.random() * 1000)}`,
//         joinDate: new Date().toISOString().split('T')[0]
//       },
//       manager: {
//         position: "Department Manager",
//         department: "Management",
//         employeeId: `MGR${Math.floor(Math.random() * 1000)}`,
//         joinDate: new Date().toISOString().split('T')[0]
//       },
//       hr: {
//         position: "HR Manager",
//         department: "Human Resources",
//         employeeId: `HR${Math.floor(Math.random() * 1000)}`,
//         joinDate: new Date().toISOString().split('T')[0]
//       },
//       admin: {
//         position: "System Administrator",
//         department: "IT Administration",
//         employeeId: `ADM${Math.floor(Math.random() * 1000)}`,
//         joinDate: new Date().toISOString().split('T')[0]
//       },
//       superadmin: {
//         position: "Super Administrator",
//         department: "Executive Management",
//         employeeId: `SA${Math.floor(Math.random() * 1000)}`,
//         joinDate: new Date().toISOString().split('T')[0]
//       }
//     };

//     const defaultData = roleData[user.role] || roleData.employee;

//     return {
//       id: user.id || Math.floor(Math.random() * 1000),
//       name: user.name || user.contact?.split('@')[0] || 'User',
//       email: user.email || user.contact,
//       contact: user.contact,
//       role: user.role,
//       userRole: user.role,
//       position: user.position || defaultData.position,
//       department: user.department || defaultData.department,
//       employeeId: user.employeeId || defaultData.employeeId,
//       joinDate: user.joinDate || defaultData.joinDate,
//       phone: user.phone || '+91 98765 43210',
//       dob: user.dob || '1990-01-01',
//       status: 'active'
//     };
//   };

//   // Demo credentials
//   const demoCredentials = [
//     { role: 'Super Admin', contact: 'superadmin@aizops.com', password: 'superadmin123' },
//     { role: 'Admin', contact: 'admin@aizops.com', password: 'admin123' },
//     { role: 'HR', contact: 'hr@aizops.com', password: 'hr123' },
//     { role: 'Manager', contact: 'manager@aizops.com', password: 'manager123' },
//     { role: 'Employee', contact: 'employee@aizops.com', password: 'employee123' }
//   ];

//   const showWelcomePopupMessage = (name, role) => {
//     setWelcomePopup({ show: true, name, role });
//     setTimeout(() => {
//       setWelcomePopup({ show: false, name: '', role: '' });
//     }, 3000);
//   };

//   const redirectToDashboard = () => {
//     setTimeout(() => {
//       navigate('/dashboard');
//     }, 500);
//   };

//   const getCurrentLoggedInUser = () => {
//     const token = localStorage.getItem('token');
//     const user = localStorage.getItem('user');
//     if (token && user) {
//       try {
//         return JSON.parse(user);
//       } catch (e) {
//         return null;
//       }
//     }
//     return null;
//   };

//   const validate = () => {
//     const newErrors = {};

//     if (activeForm === "login") {
//       if (!loginData.contact.trim()) {
//         newErrors.contact = "Email or phone is required";
//       }
//       if (!loginData.password) {
//         newErrors.password = "Password is required";
//       }
//     }

//     if (activeForm === "signup") {
//       if (!signupData.name.trim()) {
//         newErrors.name = "Name is required";
//       } else if (signupData.name.trim().length < 2) {
//         newErrors.name = "Name must be at least 2 characters";
//       }

//       if (!signupData.contact.trim()) {
//         newErrors.contact = "Contact is required";
//       }

//       if (!signupData.password) {
//         newErrors.password = "Password is required";
//       } else if (signupData.password.length < 6) {
//         newErrors.password = "Password must be at least 6 characters";
//       }

//       if (signupData.password !== signupData.confirmPassword) {
//         newErrors.confirmPassword = "Passwords do not match";
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleLoginChange = (e) => {
//     const { name, value } = e.target;
//     setLoginData(prev => ({ ...prev, [name]: value }));
//     if (errors[name]) {
//       setErrors(prev => {
//         const newErrors = { ...prev };
//         delete newErrors[name];
//         return newErrors;
//       });
//     }
//   };

//   const handleSignupChange = (e) => {
//     const { name, value } = e.target;
//     setSignupData(prev => ({ ...prev, [name]: value }));
//     if (errors[name]) {
//       setErrors(prev => {
//         const newErrors = { ...prev };
//         delete newErrors[name];
//         return newErrors;
//       });
//     }
//   };

//   const fillDemoCredentials = (contact, password) => {
//     setLoginData({ contact, password });
//     setErrors({});
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     setErrors({});
//     setSuccess('');

//     if (!validate()) {
//       return;
//     }

//     setLoading(true);

//     try {
//       let result;

//       if (activeForm === "login") {
//         result = await authService.login({
//           contact: loginData.contact,
//           password: loginData.password
//         });
//       } else {
//         result = await authService.signup({
//           name: signupData.name,
//           contact: signupData.contact,
//           password: signupData.password,
//           confirmPassword: signupData.confirmPassword,
//           role: signupData.role
//         });
//       }

//       if (result.success) {
//         setSuccess(result.message);
        
//         if (activeForm === "login") {
//           if (result.user && result.token) {
//             localStorage.clear();
//             const userData = getCompleteUserData(result.user);
            
//             localStorage.setItem('token', result.token);
//             localStorage.setItem('user', JSON.stringify(userData));
//             localStorage.setItem('userRole', userData.role);
//             localStorage.setItem('userContact', userData.contact);
            
//             showWelcomePopupMessage(userData.name, userData.role);
//           }
          
//           setLoginData({ contact: "", password: "" });
          
//           setTimeout(() => {
//             onClose(true);
//             setSuccess('');
//             setActiveForm('login');
//             redirectToDashboard();
//           }, 1500);
//         } else {
//           setLoginData({ 
//             contact: signupData.contact, 
//             password: "" 
//           });
          
//           setSignupData({ 
//             name: "", 
//             contact: "", 
//             password: "", 
//             confirmPassword: "", 
//             role: "employee" 
//           });
          
//           setSuccess(`Account created successfully! Please login with your credentials.`);
          
//           setTimeout(() => {
//             setActiveForm('login');
//             setSuccess('');
//           }, 2000);
//         }
//       } else {
//         if (result.errors) {
//           setErrors(result.errors);
//         } else {
//           setErrors({ general: result.message || 'Something went wrong' });
//         }
//       }
//     } catch (error) {
//       console.error('Auth error:', error);
//       setErrors({ 
//         general: 'Network error. Please check if the server is running.' 
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const switchForm = (form) => {
//     setActiveForm(form);
//     setErrors({});
//     setSuccess('');
//     if (form === "login") {
//       setLoginData({ contact: "", password: "" });
//     } else {
//       setSignupData({ name: "", contact: "", password: "", confirmPassword: "", role: "employee" });
//     }
//   };

//   const handleClose = () => {
//     onClose(false);
//     setActiveForm('login');
//     setErrors({});
//     setSuccess('');
//   };

//   const handleLogout = () => {
//     localStorage.clear();
//     setLoginData({ contact: "", password: "" });
//     setSignupData({ name: "", contact: "", password: "", confirmPassword: "", role: "employee" });
//     setActiveForm('login');
//     setErrors({});
//     setSuccess('');
//     navigate('/');
//     window.location.reload();
//   };

//   if (!isOpen) return null;

//   const currentUser = getCurrentLoggedInUser();
//   const currentImage = slideshowImages[currentImageIndex];

//   return (
//     <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
//       {/* Backdrop */}
//       <div
//         className="absolute inset-0 bg-black/80 backdrop-blur-sm"
//         onClick={handleClose}
//       ></div>

//       {/* Welcome Popup */}
//       {welcomePopup.show && (
//         <div className="fixed top-5 right-5 z-50 animate-slide-in-right">
//           <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl">
//             <div className="flex items-center gap-3">
//               <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                 </svg>
//               </div>
//               <div>
//                 <p className="font-bold">Welcome {welcomePopup.name}!</p>
//                 <p className="text-sm opacity-90">Role: {welcomePopup.role.toUpperCase()}</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Modal */}
//       <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_0.3s_ease-out] mx-4">
        
//         {/* Close button */}
//         <button
//           onClick={handleClose}
//           className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 text-black transition-colors"
//         >
//           ✕
//         </button>

//         {/* Two Column Grid */}
//         <div className="grid grid-cols-1 md:grid-cols-2">
          
//           {/* Left Column - Animated Image Slideshow */}
// <div className="relative hidden md:flex flex-col items-center justify-center bg-gradient-to-br from-[#6366F1] to-[#000000] overflow-hidden min-h-[500px]">
  
//   {/* Animated Background Elements */}
//   <div className="absolute inset-0">
//     {/* Subtle Grid Pattern */}
//     <div className="absolute inset-0 opacity-10" style={{
//       backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
//       backgroundSize: '40px 40px'
//     }} />
    
//     {/* Floating Particles */}
//     <div className="absolute inset-0">
//       {[...Array(8)].map((_, i) => (
//         <div
//           key={i}
//           className="absolute w-1 h-1 bg-white/20 rounded-full animate-float-particle"
//           style={{
//             top: `${Math.random() * 100}%`,
//             left: `${Math.random() * 100}%`,
//             animationDelay: `${i * 0.5}s`,
//             animationDuration: `${3 + Math.random() * 4}s`
//           }}
//         />
//       ))}
//     </div>
    
//     {/* Animated background circles */}
//     <div className="absolute inset-0 opacity-15">
//       <div className="absolute top-10 left-10 w-32 h-32 rounded-full border border-white/20 animate-float-slow" />
//       <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full border border-white/20 animate-float-medium" style={{ animationDelay: '1s' }} />
//       <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-white/10 animate-float-fast" style={{ animationDelay: '0.5s' }} />
//     </div>
//   </div>

//   {/* Image Container with Professional Crossfade */}
//   <div className="relative z-10 w-full flex flex-col items-center justify-center p-8">
    
//     {/* Image Container with Smooth Crossfade */}
//     <div className="relative w-64 h-64 mb-6">
//       {/* Current Image */}
//       <img
//         src={currentImage.src}
//         alt={currentImage.title}
//         className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl border-2 border-white/20 transition-all duration-700 ease-in-out ${
//           isAnimating ? 'opacity-0 scale-100' : 'opacity-100 scale-100'
//         }`}
//         style={{ transitionProperty: 'opacity' }}
//       />
      
//       {/* Subtle Glow Effect Behind Image */}
//       <div className={`absolute -inset-4 rounded-3xl bg-gradient-to-r from-[#6366F1]/20 to-purple-500/20 blur-xl transition-opacity duration-700 ${
//         isAnimating ? 'opacity-0' : 'opacity-100'
//       }`} />
      
//       {/* Gradient Overlay */}
//       <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      
//       {/* Subtle Border Glow on Hover */}
//       <div className="absolute inset-0 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none">
//         <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#6366F1]/30 to-purple-500/30 blur-md" />
//       </div>
//     </div>
    
//     {/* Content with Smooth Transition */}
//     <div className={`text-center transition-all duration-500 ${
//       isAnimating ? 'opacity-0 transform translate-y-3' : 'opacity-100 transform translate-y-0'
//     }`}>
//       <div className="mb-2 overflow-hidden">
//         <span className="text-xs font-semibold text-white/60 tracking-wider inline-block animate-slide-up">
//           {currentImage.tagline}
//         </span>
//       </div>
//       <h3 className="text-2xl font-bold text-white mb-2">
//         {currentImage.title}
//       </h3>
//       <p className="text-white/70 text-sm max-w-xs mx-auto leading-relaxed">
//         {currentImage.description}
//       </p>
//     </div>

//     {/* AIZops Logo with Simple Animation */}
//     <div className="mt-8 text-center">
//       <div className="flex items-center justify-center gap-2 mb-3 group cursor-pointer">
//         <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110 group-hover:rotate-3">
//           <span className="text-white font-bold text-lg">A</span>
//         </div>
//         <span className="text-white font-bold text-xl tracking-wider">AIZOPS</span>
//       </div>
      
//       {/* Simple Decorative Line */}
//       <div className="w-12 h-0.5 bg-white/20 mx-auto rounded-full transition-all duration-300 group-hover:w-20" />
//     </div>

//   </div>
// </div>

//           {/* Right Column - Form */}
//           <div className="p-6 sm:p-8 md:p-10 bg-white max-h-[90vh] overflow-y-auto">
            
//             {/* Demo Credentials Section */}
         
//             {/* Session Info Banner */}
//             {currentUser && (
//               <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
//                 <div className="flex items-center justify-between flex-wrap gap-2">
//                   <div className="flex items-center gap-2">
//                     <span className="text-sm text-yellow-800">
//                       Currently logged in as:
//                     </span>
//                     <strong className="text-[#6366F1] text-sm">
//                       {currentUser.name || currentUser.contact}
//                     </strong>
//                     <span className="text-xs bg-[#6366F1]/10 text-[#6366F1] px-2 py-0.5 rounded-full">
//                       {currentUser.role?.toUpperCase()}
//                     </span>
//                   </div>
//                   <button
//                     onClick={handleLogout}
//                     className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg hover:bg-red-600 transition"
//                   >
//                     Logout
//                   </button>
//                 </div>
//               </div>
//             )}

//             {/* Success message */}
//             {success && (
//               <div className={`mb-4 p-3 rounded-lg text-sm ${
//                 success.includes('Please login') 
//                   ? 'bg-blue-50 border border-blue-200 text-blue-700'
//                   : 'bg-green-50 border border-green-200 text-green-700'
//               }`}>
//                 {success}
//               </div>
//             )}

//             {/* Error message */}
//             {errors.general && (
//               <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
//                 {errors.general}
//               </div>
//             )}

//             {/* Login Form */}
//             {activeForm === "login" && (
//               <form onSubmit={handleSubmit}>
//                 <div className="text-center mb-6">
//                   <h3 className="text-2xl font-bold text-black">
//                     Welcome Back
//                   </h3>
//                   <p className="text-gray-500 text-sm mt-1">
//                     Sign in to your AIZOPS account
//                   </p>
//                 </div>

//                 <div className="space-y-4">
//                   <div>
//                     <input
//                       type="text"
//                       name="contact"
//                       placeholder="Email or Phone"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.contact ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={loginData.contact}
//                       onChange={handleLoginChange}
//                       disabled={loading}
//                     />
//                     {errors.contact && (
//                       <p className="text-red-500 text-xs mt-1">{errors.contact}</p>
//                     )}
//                   </div>

//                   <div>
//                     <input
//                       type="password"
//                       name="password"
//                       placeholder="Password"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.password ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={loginData.password}
//                       onChange={handleLoginChange}
//                       disabled={loading}
//                     />
//                     {errors.password && (
//                       <p className="text-red-500 text-xs mt-1">{errors.password}</p>
//                     )}
//                   </div>

//                   <button 
//                     type="submit"
//                     disabled={loading}
//                     className="w-full py-3 rounded-lg bg-[#6366F1] hover:bg-[#4f52e0] text-white font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
//                   >
//                     {loading ? 'Processing...' : 'Sign In'}
//                   </button>

//                   <p className="text-center text-sm text-gray-600">
//                     Don't have an account?{' '}
//                     <button
//                       type="button"
//                       className="text-[#6366F1] hover:underline font-medium"
//                       onClick={() => switchForm("signup")}
//                     >
//                       Sign up
//                     </button>
//                   </p>
//                 </div>
//               </form>
//             )}

//             {/* Signup Form */}
//             {activeForm === "signup" && (
//               <form onSubmit={handleSubmit}>
//                 <div className="text-center mb-6">
//                   <h3 className="text-2xl font-bold text-black">
//                     Create Account
//                   </h3>
//                   <p className="text-gray-500 text-sm mt-1">
//                     Join AIZOPS HR Management Platform
//                   </p>
//                   <p className="text-xs text-blue-600 mt-2">
//                     After signup, please login with your credentials
//                   </p>
//                 </div>

//                 <div className="space-y-3">
//                   <div>
//                     <input
//                       type="text"
//                       name="name"
//                       placeholder="Full Name"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.name ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={signupData.name}
//                       onChange={handleSignupChange}
//                       disabled={loading}
//                     />
//                     {errors.name && (
//                       <p className="text-red-500 text-xs mt-1">{errors.name}</p>
//                     )}
//                   </div>

//                   <div>
//                     <input
//                       type="text"
//                       name="contact"
//                       placeholder="Email or Phone"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.contact ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={signupData.contact}
//                       onChange={handleSignupChange}
//                       disabled={loading}
//                     />
//                     {errors.contact && (
//                       <p className="text-red-500 text-xs mt-1">{errors.contact}</p>
//                     )}
//                   </div>

//                   <div>
//                     <input
//                       type="password"
//                       name="password"
//                       placeholder="Password (min 6 characters)"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.password ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={signupData.password}
//                       onChange={handleSignupChange}
//                       disabled={loading}
//                     />
//                     {errors.password && (
//                       <p className="text-red-500 text-xs mt-1">{errors.password}</p>
//                     )}
//                   </div>

//                   <div>
//                     <input
//                       type="password"
//                       name="confirmPassword"
//                       placeholder="Confirm Password"
//                       className={`w-full px-4 py-3 rounded-lg border ${
//                         errors.confirmPassword ? 'border-red-400' : 'border-gray-300'
//                       } text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition`}
//                       value={signupData.confirmPassword}
//                       onChange={handleSignupChange}
//                       disabled={loading}
//                     />
//                     {errors.confirmPassword && (
//                       <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
//                     )}
//                   </div>

//                   {/* Role Selection */}
//                   <div>
//                     <select
//                       name="role"
//                       className="w-full px-4 py-3 rounded-lg border border-gray-300 text-black bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent transition"
//                       value={signupData.role}
//                       onChange={handleSignupChange}
//                       disabled={loading}
//                     >
//                       <option value="employee">Employee</option>
//                       <option value="hr">HR</option>
//                       <option value="manager">Manager</option>
//                       <option value="admin">Admin</option>
//                       <option value="superadmin">Super Admin</option>
//                     </select>
//                     <p className="text-gray-400 text-xs mt-1">
//                       Select your role during registration
//                     </p>
//                   </div>

//                   <button 
//                     type="submit"
//                     disabled={loading}
//                     className="w-full py-3 rounded-lg bg-[#6366F1] hover:bg-[#4f52e0] text-white font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
//                   >
//                     {loading ? 'Creating Account...' : 'Create Account'}
//                   </button>

//                   <p className="text-center text-sm text-gray-600">
//                     Already have an account?{' '}
//                     <button
//                       type="button"
//                       className="text-[#6366F1] hover:underline font-medium"
//                       onClick={() => switchForm("login")}
//                     >
//                       Sign in
//                     </button>
//                   </p>
//                 </div>
//               </form>
//             )}
//           </div>
//         </div>
//       </div>

// <style>{`
//   /* Modal Animations */
//   @keyframes fadeIn {
//     from {
//       opacity: 0;
//       transform: scale(0.95);
//     }
//     to {
//       opacity: 1;
//       transform: scale(1);
//     }
//   }
  
//   @keyframes slide-in-right {
//     from {
//       transform: translateX(100%);
//       opacity: 0;
//     }
//     to {
//       transform: translateX(0);
//       opacity: 1;
//     }
//   }
  
//   .animate-slide-in-right {
//     animation: slide-in-right 0.3s ease-out;
//   }
  
//   /* Floating Circle Animations */
//   @keyframes float-slow {
//     0%, 100% {
//       transform: translateY(0) translateX(0) scale(1);
//       opacity: 0.15;
//     }
//     33% {
//       transform: translateY(-20px) translateX(10px) scale(1.05);
//       opacity: 0.25;
//     }
//     66% {
//       transform: translateY(10px) translateX(-10px) scale(0.95);
//       opacity: 0.2;
//     }
//   }
  
//   @keyframes float-medium {
//     0%, 100% {
//       transform: translateY(0) translateX(0) scale(1);
//       opacity: 0.12;
//     }
//     33% {
//       transform: translateY(15px) translateX(-15px) scale(1.08);
//       opacity: 0.22;
//     }
//     66% {
//       transform: translateY(-15px) translateX(15px) scale(0.92);
//       opacity: 0.18;
//     }
//   }
  
//   @keyframes float-fast {
//     0%, 100% {
//       transform: translateY(0) translateX(0) scale(1);
//       opacity: 0.1;
//     }
//     33% {
//       transform: translateY(-10px) translateX(-20px) scale(1.1);
//       opacity: 0.2;
//     }
//     66% {
//       transform: translateY(10px) translateX(20px) scale(0.9);
//       opacity: 0.15;
//     }
//   }
  
//   @keyframes float-particle {
//     0%, 100% {
//       transform: translateY(0) translateX(0);
//       opacity: 0;
//     }
//     20% {
//       opacity: 0.5;
//     }
//     80% {
//       opacity: 0.3;
//     }
//     100% {
//       transform: translateY(-100px) translateX(50px);
//       opacity: 0;
//     }
//   }
  
//   @keyframes slide-up {
//     0% {
//       transform: translateY(20px);
//       opacity: 0;
//     }
//     100% {
//       transform: translateY(0);
//       opacity: 1;
//     }
//   }
  
//   /* Animation Classes */
//   .animate-float-slow {
//     animation: float-slow 8s ease-in-out infinite;
//   }
  
//   .animate-float-medium {
//     animation: float-medium 6s ease-in-out infinite;
//   }
  
//   .animate-float-fast {
//     animation: float-fast 4s ease-in-out infinite;
//   }
  
//   .animate-float-particle {
//     animation: float-particle 4s ease-in-out infinite;
//   }
  
//   .animate-slide-up {
//     animation: slide-up 0.5s ease-out;
//   }
  
//   /* Professional Image Transition */
//   .transition-all {
//     transition-property: all;
//     transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
//   }
  
//   .duration-700 {
//     transition-duration: 700ms;
//   }
  
//   .duration-500 {
//     transition-duration: 500ms;
//   }
  
//   /* Smooth Blur Effects */
//   .blur-xl {
//     filter: blur(24px);
//   }
  
//   /* Custom Scrollbar */
//   .overflow-y-auto::-webkit-scrollbar {
//     width: 6px;
//   }
  
//   .overflow-y-auto::-webkit-scrollbar-track {
//     background: #f1f1f1;
//     border-radius: 10px;
//   }
  
//   .overflow-y-auto::-webkit-scrollbar-thumb {
//     background: #6366F1;
//     border-radius: 10px;
//   }
  
//   .overflow-y-auto::-webkit-scrollbar-thumb:hover {
//     background: #4f52e0;
//   }
  
//   /* Glassmorphism Effect for Cards (Optional) */
//   .glass-effect {
//     background: rgba(255, 255, 255, 0.05);
//     backdrop-filter: blur(10px);
//     border: 1px solid rgba(255, 255, 255, 0.1);
//   }
// `}</style>
//     </div>
//   );
// };

// export default AuthModal;